import {
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";

import type {
  MeetingConfig,
  PatientData,
  RoomCache,
} from "./types";
import type { MediaStream } from "../index-types";

type RoomCacheContextValue = {
  roomCache?: RoomCache;
  setRoomCache: React.Dispatch<
    React.SetStateAction<RoomCache | undefined>
  >;

  meetingConfig?: MeetingConfig;
  setMeetingConfig: React.Dispatch<
    React.SetStateAction<MeetingConfig | undefined>
  >;

  patientData?: PatientData;
  setPatientData: React.Dispatch<
    React.SetStateAction<PatientData | undefined>
  >;

  mediaStream?: MediaStream;
  setMediaStream: React.Dispatch<
    React.SetStateAction<MediaStream | undefined>
  >;
};

type LoadingContextValue = {
  loading: boolean;
  setShowLoading: React.Dispatch<
    React.SetStateAction<boolean>
  >;

  loadingText: string;
  setLoadingText: React.Dispatch<
    React.SetStateAction<string>
  >;

  activateTimer: boolean;
  setActivateTimer: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

export const RoomCacheContext =
  createContext<RoomCacheContextValue | null>(null);

export const LoadingContext =
  createContext<LoadingContextValue | null>(null);

type Props = {
  children: React.ReactNode;
};

export function useRoomCacheContext() {
  const context = useContext(RoomCacheContext);

  if (!context) {
    throw new Error(
      "useRoomCacheContext must be used within RoomCacheProvider"
    );
  }

  return context;
}

export function useLoadingContext() {
  const context = useContext(LoadingContext);

  if (!context) {
    throw new Error(
      "useLoadingContext must be used within RoomCacheProvider"
    );
  }

  return context;
}

const RoomCacheProvider = ({ children }: Props) => {
  const [roomCache, setRoomCache] =
    useState<RoomCache | undefined>();

  const [meetingConfig, setMeetingConfig] =
    useState<MeetingConfig | undefined>();

  const [patientData, setPatientData] =
    useState<PatientData | undefined>();

  const [mediaStream, setMediaStream] =
    useState<MediaStream | undefined>();

  const [activateTimer, setActivateTimer] =
    useState(false);

  const [loading, setShowLoading] =
    useState(false);

  const [loadingText, setLoadingText] =
    useState("Loading...");

  /*
   * This value does not contain loading state, so showing or hiding the
   * loading overlay will not rerender Consult or its Zoom video players.
   */
  const roomValue = useMemo<RoomCacheContextValue>(
    () => ({
      roomCache,
      setRoomCache,
      meetingConfig,
      setMeetingConfig,
      patientData,
      setPatientData,
      mediaStream,
      setMediaStream,
    }),
    [
      roomCache,
      meetingConfig,
      patientData,
      mediaStream,
    ]
  );

  const loadingValue = useMemo<LoadingContextValue>(
    () => ({
      loading,
      setShowLoading,
      loadingText,
      setLoadingText,
      activateTimer,
      setActivateTimer,
    }),
    [
      loading,
      loadingText,
      activateTimer,
    ]
  );

  return (
    <LoadingContext.Provider value={loadingValue}>
      <RoomCacheContext.Provider value={roomValue}>
        {children}
      </RoomCacheContext.Provider>
    </LoadingContext.Provider>
  );
};

export default RoomCacheProvider;